// Lib
import { useCallback, useEffect, useRef, useState } from 'react';
import { delay } from 'lodash';

// Utils
import {
    hasProgressiveImageFailed,
    hasProgressiveImageLoaded,
    markProgressiveImageAsFailed,
    markProgressiveImageAsLoaded,
} from './progressiveImagesSingleton';

const IMAGE_FADE_TIMEOUT = 300;

/**
 * Shows an image which progressively becomes better quality by first showing a thumbnail of the image,
 * and then, once the larger version has loaded, replaces the thumbnail with the full size image.
 *
 * NOTE: The component initially shows the source file because it would otherwise cause a flash of content when dragging
 * an element that has already loaded.
 * Now there will be a slight flash when the image has not yet loaded, but that's an acceptable trade off.
 *
 * NOTE: Every platform renders the remote URL directly. Permission-checked media authenticates from
 * cookies alone — the legacy `mn-token` cookie (written into the native cookie store by the MNToken
 * plugin as `SameSite=None; Secure`) or the Better Auth session cookie — so a plain <img> load needs
 * no request headers, and therefore no fetch-the-bytes-into-a-blob detour on Capacitor.
 */
export default ({ source: imageSourceUrl, onImageLoadCb }) => {
    const imageRef = useRef();
    const currentImageSourceUrlRef = useRef(imageSourceUrl);
    const hasUnmountedRef = useRef(false);

    const [renderedImageUrl, setRenderedImageUrl] = useState(imageSourceUrl);
    const [hasLoaded, setHasLoaded] = useState(hasProgressiveImageLoaded(imageSourceUrl));
    const [isError, setIsError] = useState(hasProgressiveImageFailed(imageSourceUrl));

    const callImageLoadCb = useCallback((imageToRenderUrl, onImageLoadFn) => {
        // Make sure the transparent fade has finished before switching the image.
        delay(() => onImageLoadFn && onImageLoadFn(imageToRenderUrl, imageRef.current), IMAGE_FADE_TIMEOUT);
    }, []);

    const setLoadedState = useCallback(
        (progressiveImageSourceUrl, onImageLoadFn) => {
            setIsError(false);

            // If this is the first time we've loaded this image source, call the load callback.
            if (!hasProgressiveImageLoaded(progressiveImageSourceUrl)) {
                callImageLoadCb(progressiveImageSourceUrl, onImageLoadFn);
            }

            setRenderedImageUrl(progressiveImageSourceUrl);
            setHasLoaded(true);
        },
        [callImageLoadCb],
    );

    const preloadThenShowImage = useCallback(
        (newImageSourceUrl, onImageLoadFn) => {
            const onLoad = () => {
                if (hasUnmountedRef.current) return;

                // Only show this image if it still matches the latest requested image source.
                // Otherwise a slower stale request can overwrite the current image.
                if (currentImageSourceUrlRef.current === newImageSourceUrl) {
                    setLoadedState(newImageSourceUrl, onImageLoadFn);
                }

                // Mark the original image source as loaded even if a newer request is now current.
                markProgressiveImageAsLoaded({ source: newImageSourceUrl });
            };

            const onError = () => {
                if (hasUnmountedRef.current) return;

                if (currentImageSourceUrlRef.current !== newImageSourceUrl) return;

                setIsError(true);

                // Treat an error as a completed load so callers can finish their loading state.
                callImageLoadCb(newImageSourceUrl, onImageLoadFn);

                markProgressiveImageAsFailed({ source: newImageSourceUrl });
            };

            try {
                // Don't reload an image source that is already loaded for the current request.
                if (
                    currentImageSourceUrlRef.current === newImageSourceUrl &&
                    hasProgressiveImageLoaded(newImageSourceUrl)
                )
                    return;

                // Track the latest requested image source so stale async work can be ignored.
                currentImageSourceUrlRef.current = newImageSourceUrl;

                // Create an in-memory image element and assign the image URL so it loads in the background.
                imageRef.current = new Image();
                // Apparently the onLoad event won't fire if it's added to the image AFTER the src is set and
                // the image is loaded from the cache, but if you add it before it WILL.
                // Unfortunately, my empirical evidence suggests that's not the case in Chrome, but I'll add it
                // in this order anyway
                imageRef.current.onload = onLoad;
                imageRef.current.onerror = onError;
                imageRef.current.src = newImageSourceUrl;

                // If the image is loaded from cache, then it will be "complete" immediately, so trigger onLoad manually.
                // I've again seen inconsistent results with this in Chrome, but overall it seems to work.
                if (imageRef.current.complete) onLoad();
            } catch {
                onError();
            }
        },
        [callImageLoadCb, setLoadedState],
    );

    useEffect(() => {
        hasUnmountedRef.current = false;

        // Remove any onload callbacks on un-mount
        return () => {
            hasUnmountedRef.current = true;

            if (imageRef.current) {
                imageRef.current.onload = () => null;
                imageRef.current.onerror = () => null;
            }
        };
    }, []);

    // When the requested image source changes, load the new image in the background.
    useEffect(() => {
        // If the requested image source is already loaded, don't load it again.
        // e.g. dropping an existing image element onto a link replace the preview
        if (hasProgressiveImageLoaded(imageSourceUrl)) {
            currentImageSourceUrlRef.current = imageSourceUrl;
            setLoadedState(imageSourceUrl, onImageLoadCb);
            return;
        }

        setHasLoaded(false);
        preloadThenShowImage(imageSourceUrl, onImageLoadCb);
    }, [preloadThenShowImage, onImageLoadCb, setLoadedState, imageSourceUrl]);

    return {
        hideImage: !hasLoaded,
        visibleSource: renderedImageUrl,
        isError,
    };
};
